import { InsightFeedKey, InsightType, RequestStatus, defaults, entities } from 'typed/api';
import {
    childrenReducer, combineReducers, entityAddReducer, entityDeleteReducer, entityLoadAllReducer, networkActionReducer, switchReducers, synchronizableEnumReducer
} from './utils';
import { deepAssign } from 'typed/utils';

const
    insightFeedKeys = Object.values(InsightFeedKey).unique(),
    ifs = Object.fromEntries(insightFeedKeys.map(key => [key, defaults.NetworkArrayOf(defaults.InsightReport) as entities.InsightNetworkArray])),
    ipc = defaults.NetworkArrayOf(defaults.InsightReport) as entities.InsightNetworkArray,
    irl = [] as entities.InsightReactionLink[],
    iimg = [] as entities.InsightImage[],
    ided = defaults.NetworkArrayOf(defaults.InsightDefaultExcludedDomain),
    it = defaults.NetworkArrayOf(defaults.InsightType),

    insightFeedReducer = switchReducers(ipc,
        networkActionReducer(ipc, 'SEARCH_ROOT_INSIGHTS_WITH_REPLIES'),
        networkActionReducer(ipc, 'SEARCH_ROOT_INSIGHTS_WITH_REPLIES_BY_FILTER'),
        networkActionReducer(ipc, 'SEARCH_AGENDA_FEED_ROOT_INSIGHTS_WITH_REPLIES'),
        networkActionReducer(ipc, 'LOAD_ROOT_INSIGHT_WITH_REPLIES'),
        networkActionReducer(ipc, 'LOAD_ROOT_INSIGHT_WITH_REPLIES_BY_INSIGHT_ID'),
        childrenReducer(ipc,
            combineReducers(defaults.InsightReport, {
                Reactions: switchReducers(irl,
                    entityAddReducer(irl, 'ADD_INSIGHT_REACTION_LINK'),
                    entityDeleteReducer(irl, 'DELETE_INSIGHT_REACTION_LINK')
                ),
                Images: (state, action) => {
                    const newState = state || iimg;

                    switch (action.type) {
                        case 'LOAD_INSIGHT_IMAGE_ATTACHMENT_DATA':
                            const imageIndex = newState.getIndexByID(action.payload.insightImageID);

                            return deepAssign(newState, {
                                [imageIndex]: {
                                    Custom: {
                                        imageDataPending: action.payload.status === RequestStatus.PENDING,
                                        imageData: action.payload.status === RequestStatus.SUCCEEDED ? action.payload.response : undefined
                                    }
                                }
                            });

                        default:
                            return newState;
                    }
                }
            }),
            (state, action) => state.Data.getIndexByID(action.payload && (action.payload as any).parentID)
        ),
        (state, action) => {
            const newState = state || ipc;

            if (action.type === 'INITIALIZE_INSIGHTS_FEED') {
                return deepAssign(newState, {
                    Custom: {
                        feedInitialized: true,
                        feedKey: action.payload.feedKey,
                        currentUserID: action.payload.currentUserID
                    }
                });
            }

            if (action.type === 'DROP_INSIGHTS_FEED' ||
                action.type === 'ADD_STRATEGIC_GOAL_MILESTONE_STATUS_UPDATE' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(newState, {
                    Data: [],
                    Custom: {
                        feedInitialized: false,
                        feedKey: undefined,
                        initializedFilters: undefined,
                        currentUserID: undefined,
                        status: RequestStatus.UNSYNCED
                    }
                });
            }

            if (action.type === 'SET_SELECTED_INSIGHTS_FEED_FILTER') {
                return deepAssign(newState, {
                    Data: [],
                    Custom: {
                        initializedFilters: undefined,
                        selectedFilter: action.payload.filter,
                        status: RequestStatus.UNSYNCED
                    }
                });
            }

            if (action.type === 'TRANSLATE_INSIGHT') {
                return deepAssign(newState, {
                    Custom: {
                        initializedFilters: undefined,
                        selectedFilter: action.payload.filter,
                        status: RequestStatus.UNSYNCED
                    }
                });
            }

            if (action.type === 'INITIALIZE_FILTERED_INSIGHTS' && action.payload.filter) {
                const
                    oldFilter = newState.Custom.initializedFilters || { ...defaults.InsightFilter },
                    newFilter = { ...defaults.InsightFilter };

                Object.keys(newFilter)
                    .map(filterKey => filterKey as any as string)
                    .filter((filterKey: string) => {
                        const filterValue: any = (oldFilter as any)[filterKey] as any;
                        return Array.isArray(filterValue);
                    })
                    .forEach((filterKey: string) => {
                        const
                            filterValues: number[] = (oldFilter as any)[filterKey] as any as number[],
                            loadedValues: number[] = (action.payload.filter as any)[filterKey] as any as number[],

                            oldValues = filterValues ? filterValues : [],
                            newValues = loadedValues ? loadedValues : [];

                        (newFilter as any)[filterKey] = [...oldValues, ...newValues].filter((value, index, array) => array.indexOf(value) === index);
                    });

                return deepAssign(newState, {
                    Custom: {
                        feedInitialized: true,
                        feedKey: action.payload.feedKey,
                        initializedFilters: { ...newFilter },
                        currentUserID: action.payload.currentUserID
                    }
                });
            }

            if (action.type === 'LOAD_ROOT_INSIGHT_WITH_REPLIES' && action.payload.status === RequestStatus.SUCCEEDED) {
                if (action.payload.response.length === 0) {
                    throw new Error(`Insight does not exist ${action.payload.insightID}`);
                }
            }

            if ([
                'SEARCH_ROOT_INSIGHTS_WITH_REPLIES',
                'SEARCH_ROOT_INSIGHTS_WITH_REPLIES_BY_FILTER',
                'SEARCH_AGENDA_FEED_ROOT_INSIGHTS_WITH_REPLIES',
                'LOAD_ROOT_INSIGHT_WITH_REPLIES',
                'LOAD_ROOT_INSIGHT_WITH_REPLIES_BY_INSIGHT_ID',
                'LOAD_MANY_ROOT_INSIGHTS_WITH_REPLIES'
            ].includes(action.type)) {

                if (!newState.Custom.feedInitialized) {
                    return deepAssign(newState, {
                        Custom: { status: RequestStatus.UNSYNCED }
                    });
                }

                if (action.payload.status !== RequestStatus.SUCCEEDED) {
                    return deepAssign(newState, {
                        Custom: { status: action.payload.status }
                    });
                }

                if (newState.Data.length === 0) {
                    return deepAssign(newState, {
                        Data: action.payload.response || [],
                        Custom: { status: action.payload.status }
                    });
                }

                if (action.payload.response.length === 0) {
                    return deepAssign(newState, {
                        Custom: { status: action.payload.status }
                    });
                }

                const
                    insights = (action.payload.response || []) as any as entities.InsightReport[],
                    newRootItemIDs = insights.filter(({ MainParentInsightID }) => !MainParentInsightID).map(({ ID }) => ID),

                    newData = newState.Data.copy().filter(({ ID, MainParentInsightID }) => !newRootItemIDs.includes(ID) &&
                        (!MainParentInsightID || !newRootItemIDs.includes(MainParentInsightID)));

                newRootItemIDs.forEach((rootID) => {
                    const
                        newRootItem = insights.getByID(rootID)!,
                        itemsToAdd = insights.filter(({ ID, MainParentInsightID }) => ID === rootID || MainParentInsightID === rootID),
                        nextItemIndex = newData.findIndex(({ LastUpdate, MainParentInsightID }) => !MainParentInsightID && +LastUpdate < +newRootItem.LastUpdate),
                        insertAtIndex = nextItemIndex === -1 ? newData.length : nextItemIndex;

                    newData.splice(insertAtIndex, 0, ...itemsToAdd);
                });

                return deepAssign(newState, {
                    Data: newData,
                    Custom: { status: action.payload.status }
                });
            }

            if (action.type === 'ADD_ENTITY_CHANGE' && (action.change as any).EntityType === defaults.InsightReport.EntityType) {
                const
                    change: entities.EntityChange = action.change as any as entities.EntityChange,
                    rootInsightID = change.EntityID,
                    userID = change.UserID,
                    insightIndex = newState.Data.getIndexByID(rootInsightID);

                if (!newState.Custom.feedInitialized || newState.Custom.currentUserID === userID) {
                    return newState;
                }

                // if new insight was added, drop feed and reload again when needed
                /* if (insightIndex < 0) {
                    return deepAssign(newState, {
                        Data: [],
                        Custom: {
                            feedInitialized: false,
                            feedKey: undefined,
                            initializedFilters: undefined,
                            currentUserID: undefined,
                            status: RequestStatus.UNSYNCED
                        }
                    });
                }*/

                if (insightIndex >= 0) {
                    return deepAssign(newState, {
                        Data: {
                            [insightIndex]: {
                                Custom: { status: RequestStatus.UNSYNCED }
                            }
                        }
                    });
                }
            }

            if (action.type === 'DELETE_INSIGHT' && action.payload.status === RequestStatus.SUCCEEDED) {
                const
                    deletedEntity = action.payload.entity as any as entities.InsightReport,
                    rootInsightID = deletedEntity.MainParentInsightID || deletedEntity.ID;

                return deepAssign(newState, {
                    Data: newState.Data.filter(({ ID, MainParentInsightID }) => ID !== rootInsightID && MainParentInsightID !== rootInsightID),
                    Custom: { status: action.payload.status }
                });
            }

            if (action.type === 'EDIT_INSIGHT' && action.payload.status === RequestStatus.SUCCEEDED) {
                const
                    editedEntity = action.payload.entity as any as entities.InsightReport,
                    insightIndex = newState.Data.getIndexByID(editedEntity.ID);

                if (insightIndex >= 0) {
                    return deepAssign(newState, {
                        Data: {
                            [insightIndex]: editedEntity
                        }
                    });
                }
            }

            if ((action.type === 'SET_WORKSHOP_LANGUAGE_CODE' || action.type === 'CLEAR_WORKSHOP_LANGUAGE_CODE') && action.payload.status === RequestStatus.SUCCEEDED) {
                return ipc;
            }

            return newState;
        }
    );

export default combineReducers({
    insights: (state, action) => {
        let nextState = state || ifs;

        if (action.payload && Object.prototype.hasOwnProperty.call(action.payload, 'feedKey')) {
            nextState = deepAssign(nextState, { [action.payload.feedKey]: insightFeedReducer(nextState[action.payload.feedKey], action) });
        }
        else {
            for (const feedKey in nextState) {
                if (Object.prototype.hasOwnProperty.call(nextState, feedKey)) {
                    nextState = deepAssign(nextState, { [feedKey]: insightFeedReducer(nextState[feedKey], action) });

                    const isCustomFeed = !insightFeedKeys.includes(feedKey as any);
                    if (isCustomFeed && action.type === 'ADD_ENTITY_CHANGE' && action.change.EntityType === defaults.InsightReport.EntityType) {
                        nextState = deepAssign(nextState, {
                            [feedKey]: {
                                Custom: {
                                    status: RequestStatus.UNSYNCED
                                }
                            }
                        });
                    }
                }
            }
        }

        return nextState;
    },
    defaultExcludedDomains: switchReducers(ided,
        entityLoadAllReducer(ided, 'LOAD_ALL_INSIGHT_DEFAULT_EXCLUDED_DOMAINS'),
        entityAddReducer(ided, 'ADD_INSIGHT_DEFAULT_EXCLUDED_DOMAIN')
    ),
    insightTypes: synchronizableEnumReducer(it, 'LOAD_ALL_INSIGHT_TYPES', InsightType as any, 'InsightType')
});
